import { z } from "zod";
import { smbLpKeysSchema, smbLpDesignSystemIdSchema } from "./smb-lp-templates";

export const SMB_AUTOMATION_FLAG_KEY = "smb_automation";
export const ORDER_STATUSES = [
  "awaiting_intake", "intake_received", "enriching", "needs_review",
  "generating", "qa", "ready", "published", "failed", "cancelled",
] as const;
export const smbOrderStatusSchema = z.enum(ORDER_STATUSES);
export type SmbOrderStatus = z.infer<typeof smbOrderStatusSchema>;

export type SmbJson = null | boolean | number | string | SmbJson[] | { [key: string]: SmbJson };
const jsonValue: z.ZodType<SmbJson> = z.lazy(() => z.union([
  z.null(), z.boolean(), z.number().finite(), z.string().max(50000),
  z.array(jsonValue).max(500), z.record(z.string().max(120), jsonValue),
]));
// Bound depth/cycles before recursive parsing, including malformed DB JSON.
export const smbJsonObjectSchema = z.unknown().superRefine((value, ctx) => {
  const seen = new WeakSet<object>();
  let entries = 0;
  let bytes = 0;
  function bounded(input: unknown, depth: number): boolean {
    if (++entries > 5000 || depth > 12) return false;
    if (typeof input === "string") bytes += input.length;
    if (bytes > 200000) return false;
    if (input === null || typeof input !== "object") return true;
    if (seen.has(input)) return false;
    seen.add(input);
    return Object.entries(input).every(([key, item]) => {
      bytes += key.length;
      return bounded(item, depth + 1);
    });
  }
  if (!bounded(value, 0))
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "JSON exceeds the storage limit or contains cycles" });
}).pipe(z.record(z.string().max(120), jsonValue));
const ids = z.array(z.string().trim().min(1).max(160)).max(10)
  .refine(values => new Set(values).size === values.length, "Selections must be unique");
export const smbPersonaSectionSchema = z.object({
  staticAdUnitTypeIds: ids, memeTemplateIds: ids,
  slideshowStyleValues: ids, ugcStyleValues: ids,
}).strict().refine(section => Object.values(section).reduce((sum, entries) => sum + entries.length, 0) <= 10,
  "Each persona section supports at most 10 combined selections");
export const smbManifestSchema = z.object({
  personaSections: z.array(smbPersonaSectionSchema).length(3),
  minimums: z.object({
    staticAds: z.number().int().min(0).max(30), memes: z.number().int().min(0).max(30),
    slideshows: z.number().int().min(0).max(30), ugcVideos: z.number().int().min(0).max(30),
  }).strict(),
  lpTemplateCatalogKeys: smbLpKeysSchema.optional(),
  lpDesignSystemId: smbLpDesignSystemIdSchema.optional(),
}).strict();
export const smbTemplateOverridesSchema = z.object({
  add: ids, remove: ids,
  lpTemplateCatalogKeys: smbLpKeysSchema.optional(),
  lpDesignSystemId: smbLpDesignSystemIdSchema.optional(),
}).strict()
  .refine(value => !value.add.some(id => value.remove.includes(id)), "An override cannot both add and remove the same ID");
export type SmbTemplateOverrides = z.infer<typeof smbTemplateOverridesSchema>;
export const smbConfidenceFlagsSchema = z.array(z.string().min(1).max(160)).max(50);
export const smbStepStateSchema = z.object({
  status: z.enum(["pending", "running", "completed", "failed"]),
  attempt: z.number().int().nonnegative(),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  result: smbJsonObjectSchema.optional(),
  errorCode: z.string().max(80).nullable().optional(),
}).strict();
export const smbStepStatesSchema = z.record(z.string().min(1).max(80), smbStepStateSchema);
// Numeric is stored as PostgreSQL numeric and returned by pg as an exact decimal string.
export const smbMoneySchema = z.string().regex(/^\d{1,8}(?:\.\d{1,6})?$/);
export type SmbManifest = z.infer<typeof smbManifestSchema>;
export type SmbStepStates = z.infer<typeof smbStepStatesSchema>;
